import {useEffect, useState, useRef} from 'react'
import mapRequest from '@apis/map'
import {
  Flex,
  Text,
  Box,
  Stack,
  Heading,
  Icon,
  Image,
  SkeletonCircle,
  SkeletonText,
  Skeleton,
  Center,
} from '@chakra-ui/react'
import Header from './Header'
import {ReactComponent as Target} from '@assets/svgs/roadmap/target.svg'
import {Progress} from 'antd'
import {CommonColors} from '@utils/CommonStyles'
import Task from './Task'
import {AiOutlineUp, AiOutlineDown} from 'react-icons/ai'
import Modal from './Modal' // Ensure the correct case is used
import GlobalButton from '@components/GlobalButton/GlobalButton'
import DailyReviewIcon from '@assets/images/roadmap/daily-review.png'
import BackgroundDailyReview from '@assets/images/roadmap/background-daily-review.png'
import {EVENT_NAME, PATH, SCREEN_TRACKING} from '@utils/Const'
import {useNavigate} from 'react-router-dom'
import reviseRequest from '@apis/revise'
import {useDispatch, useSelector} from 'react-redux'
import {
  updateCurrentCourseId,
  updateIsShowModalPromoterScore,
} from '@redux/shared/shared'
import {ReactComponent as Crown} from '@assets/svgs/roadmap/crown.svg'
import ModalPromoterScore from '@components/ModalPromoterScore'
import {trackingEvent, trackingPage} from '@utils'
import {updateIsCompleteFirstTask} from '@redux/map/mapSlice'

const RoadmapFull = () => {
  const {dataFeedbackOverview, currentCourseId} = useSelector(
    (state: any) => state.shared,
  )
  const [roadmapData, setRoadmapData] = useState<any>()
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [openWeeks, setOpenWeeks] = useState<number[]>([]) // Ensure openWeeks is typed as an array of numbers
  const currentTaskRef = useRef<HTMLDivElement | null>(null)
  const [hasScrolled, setHasScrolled] = useState<boolean>(false) // Add a flag to track scrolling
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const [quantities, setQuantities] = useState<any>()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  useEffect(() => {
    trackingPage(SCREEN_TRACKING.ROADMAP_PAGE)
    if (quantities?.phrase_quantity + quantities?.sentence_quantity) {
      trackingEvent(EVENT_NAME.MAP_REVIEW_BUTTON_CLICK)
    }
  }, [])

  useEffect(() => {
    const fetchCourses = async () => {
      setIsLoading(true)
      setHasScrolled(false)
      try {
        const params = {
          'course-id': currentCourseId,
        }
        const res = await mapRequest.getRoadmapOverview(params)
        setRoadmapData(res?.data?.data)
        dispatch(
          updateIsCompleteFirstTask(
            res?.data?.data?.heading?.is_complete_first_task,
          ),
        )
        if (!currentCourseId) {
          dispatch(updateCurrentCourseId(res?.data?.data?.heading?.course_id))
        }
      } catch (err) {
      } finally {
        setIsLoading(false)
      }
    }
    fetchCourses()
  }, [currentCourseId])

  useEffect(() => {
    const getQuantities = async () => {
      try {
        const res = await reviseRequest.getQuantities()
        if (res?.status === 200) {
          setQuantities(res.data.data)
        }
      } catch (error) {}
    }

    getQuantities()
  }, [])

  useEffect(() => {
    if (!roadmapData) return
    if (
      checkIsAnySectionCompleted() &&
      !dataFeedbackOverview?.feedback_data?.FINISH_FIRST_SECTION?.is_completed
    ) {
      dispatch(updateIsShowModalPromoterScore(true))
    }
  }, [roadmapData])

  const checkIsAnySectionCompleted = () => {
    // Kiểm tra xem có tuần nào đã hoàn thành hay không
    return roadmapData?.body?.phases?.some((phase: any) =>
      phase.tasks_sections.some((section: any) => section.progress === 100),
    )
  }

  const handleResetCurrentTask = async () => {
    if (!roadmapData) return
    try {
      await mapRequest.resetCurrentTask()
    } catch (error) {}
  }

  useEffect(() => {
    window.addEventListener('beforeunload', handleResetCurrentTask)
    return () => {
      window.removeEventListener('beforeunload', handleResetCurrentTask)
    }
  }, [roadmapData])

  useEffect(() => {
    // Find the week containing the current task
    const currentWeek = roadmapData?.body?.phases
      ?.flatMap((phase: any) => phase.tasks_sections)
      .find((week: any) =>
        week.tasks.some(
          (task: any) => roadmapData?.body?.current_task_id === task.id,
        ),
      )

    if (currentWeek) {
      setOpenWeeks([currentWeek.sectionId])
    }
  }, [roadmapData])

  useEffect(() => {
    if (currentTaskRef.current && !hasScrolled) {
      currentTaskRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
      setHasScrolled(true)
    }
  }, [openWeeks, hasScrolled, roadmapData])

  const toggleWeek = (weekId: any) => {
    trackingEvent(EVENT_NAME.MAP_WEEK_EXPAND)
    setOpenWeeks((prev: any) =>
      prev.includes(weekId)
        ? prev.filter((i: any) => i !== weekId)
        : [...prev, weekId],
    )
  }

  const RoadmapFullSkeleton = () => (
    <Flex mt={{base: '16px', md: '32px'}} gap={'32px'}>
      <Flex flexDirection={'column'} w={'100%'} maxW={'800px'}>
        {Array.from({length: 5}).map((_, index) => (
          <Box key={index} mt={'24px'}>
            <SkeletonCircle size="48px" />
            <Box flexGrow={1} mt={'16px'}>
              <SkeletonText noOfLines={1} />
              <SkeletonText noOfLines={1} width="50%" mt={2} />
            </Box>
          </Box>
        ))}
      </Flex>
      <Flex flex={1}>
        <Skeleton w={'100%'} h={'200px'} borderRadius={'12px'} />
      </Flex>
    </Flex>
  )

  const renderDailyReview = () => (
    <Flex flex={1}>
      <Box
        w={'100%'}
        h={'fit-content'}
        p={{base: '16px', md: '24px'}}
        border={'1px solid #E4E7EC'}
        borderRadius={'12px'}
        overflow={'hidden'}>
        <Flex alignItems={'center'} justifyContent={'space-between'}>
          <Box w={'48px'} h={'48px'} position={'relative'}>
            <Image w={'100%'} h={'100%'} src={DailyReviewIcon} />
            <Box
              position={'absolute'}
              top={'50% '}
              left={'50%'}
              transform={'translate(-50%, -50%)'}
              w={'300px'}
              h={'300px'}
              bgImage={BackgroundDailyReview}
              bgSize={'cover'}
              bgPosition={'center'}
            />
          </Box>
          <Text fontSize={'32px'} fontWeight={600} color={CommonColors.primary}>
            {quantities?.phrase_quantity + quantities?.sentence_quantity || 0}
          </Text>
        </Flex>
        <Text
          mt={'16px'}
          fontSize={'12px'}
          fontWeight={700}
          textTransform={'uppercase'}
          color={'#7789A6'}>
          Daily review
        </Text>
        <Text mt={'4px'} fontSize={'14px'}>
          Hôm nay bạn có{' '}
          {quantities?.phrase_quantity + quantities?.sentence_quantity || 0} cụm
          và câu cần ôn
        </Text>
        <GlobalButton
          w={'100%'}
          py={'24px'}
          mt={'24px'}
          border={'1px solid #70BAFF'}
          borderRadius={'8px'}
          bg={'transparent'}
          color={'#007EF5'}
          _hover={{
            bg: '#007EF5',
            color: 'white',
          }}
          onClick={() => {
            trackingEvent(EVENT_NAME.MAP_DAILY_REVIEW_START)
            const path =
              quantities?.phrase_quantity > 0
                ? PATH.LEARN_REVISE
                : PATH.LEARN_REVISE_SENTENCE_OVERVIEW
            navigate(path, {
              state: {
                entry_point: 'ROADMAP',
              },
            })
          }}>
          Ôn tập ngay
        </GlobalButton>
      </Box>
    </Flex>
  )

  const getTaskStatus = (
    taskItem: any,
    currentTaskId: number,
    maxTaskOrder: number,
  ) => {
    // Nếu task đã hoàn thành
    if (taskItem.is_completed) {
      return taskItem.id === currentTaskId ? 'current' : 'completed'
    }

    if (!roadmapData?.is_course_available) {
      return 'locked'
    }

    // Nếu task chưa hoàn thành
    if (taskItem.id === currentTaskId) {
      return 'current'
    }

    // Nếu task order lớn hơn max order
    if (taskItem.task_order > maxTaskOrder) {
      return 'locked'
    }

    return undefined
  }

  return (
    <Box p={{base: '16px', md: '32px'}} position="relative">
      <Header roadmapData={roadmapData} />
      {isLoading ? (
        <RoadmapFullSkeleton />
      ) : (
        <Flex
          mt={{base: '16px', md: '32px'}}
          gap={'32px'}
          flexDirection={{base: 'column-reverse', md: 'row'}}>
          <Box className="roadmap-content" maxW={'800px'} w={'100%'}>
            {roadmapData?.body?.phases?.length > 0 ? (
              roadmapData?.body?.phases?.map((item: any, index: number) => (
                <Box key={index} className="phase-item" mb={'24px'}>
                  <Heading
                    as="h1"
                    fontSize={{base: '20px', md: '24px'}}
                    fontWeight={600}
                    color="gray.800"
                    mb={2}>
                    {item?.phase_name}
                  </Heading>
                  <Text color="gray.600" mb={6}>
                    {item?.phase_title}
                  </Text>
                  {item?.tasks_sections.length > 0 ? (
                    item?.tasks_sections?.map(
                      (weekItem: any, index: number) => (
                        <Box
                          key={index}
                          className="week-item"
                          mb={'16px'}
                          p={{base: '16px', md: '32px'}}
                          bg={'#F9FAFB'}
                          borderRadius={'24px'}>
                          <Flex
                            gap={'12px'}
                            alignItems={'center'}
                            justifyContent={'space-between'}
                            onClick={() => toggleWeek(weekItem?.sectionId)}
                            cursor="pointer">
                            <Box>
                              <Flex alignItems={'center'} gap={'8px'} mb={2}>
                                <Icon as={Target} stroke={'#339CFF'} />
                                <Text
                                  textTransform={'uppercase'}
                                  fontSize={'12px'}
                                  fontWeight={700}
                                  color={'#667085'}>
                                  {weekItem?.name}
                                </Text>
                              </Flex>
                              <Heading
                                as="h2"
                                color="gray.800"
                                mb={2}
                                fontSize={{base: '16px', md: '18px'}}
                                fontWeight={600}>
                                {weekItem?.title}
                              </Heading>
                              <Text
                                color="gray.600"
                                mb={2}
                                fontSize={{base: '12px', md: '14px'}}>
                                {weekItem?.description}
                              </Text>
                            </Box>
                            {weekItem?.isPremium ? (
                              <Center
                                flexShrink={0}
                                w={'56px'}
                                h={'56px'}
                                borderRadius={'50%'}
                                bg={'#FFF3E0'}>
                                <Icon as={Crown} w={'30px'} h={'30px'} />
                              </Center>
                            ) : (
                              <Progress
                                type="circle"
                                percent={weekItem?.progress}
                                size={64}
                                strokeWidth={10}
                                strokeColor={CommonColors.primary}
                              />
                            )}
                          </Flex>
                          <Flex
                            alignItems="center"
                            cursor="pointer"
                            onClick={() => toggleWeek(weekItem?.sectionId)}>
                            <Text
                              color="blue.500"
                              mr={2}
                              fontSize={{base: '12px', md: '14px'}}>
                              {openWeeks.includes(weekItem?.sectionId)
                                ? 'Ẩn bớt'
                                : 'Xem chi tiết'}
                            </Text>
                            <Icon
                              as={
                                openWeeks.includes(weekItem?.sectionId)
                                  ? (AiOutlineUp as any)
                                  : (AiOutlineDown as any)
                              }
                              color="blue.500"
                            />
                          </Flex>
                          {openWeeks.includes(weekItem?.sectionId as any) && (
                            <Stack
                              className="task-list"
                              spacing={'14px'}
                              mt={'16px'}>
                              {weekItem?.tasks?.length > 0 ? (
                                weekItem?.tasks?.map(
                                  (taskItem: any, index: number) => (
                                    <Task
                                      key={index}
                                      index={index}
                                      week_name={weekItem?.name}
                                      is_finish_week={false}
                                      is_summary={false}
                                      is_lock={taskItem?.is_lock}
                                      task_id={taskItem?.id}
                                      image={taskItem?.image}
                                      type={taskItem?.type}
                                      input_params={taskItem?.input_params}
                                      title={taskItem?.name}
                                      is_course_available={
                                        roadmapData?.is_course_available
                                      }
                                      duration={`${taskItem?.estimate_time} min`}
                                      status={getTaskStatus(
                                        taskItem,
                                        roadmapData?.body?.current_task_id,
                                        roadmapData?.body?.max_task_order,
                                      )}
                                      ref={
                                        roadmapData?.body?.current_task_id ===
                                        taskItem?.id
                                          ? currentTaskRef
                                          : null
                                      }
                                    />
                                  ),
                                )
                              ) : (
                                <Text>Không có bài học</Text>
                              )}
                            </Stack>
                          )}
                        </Box>
                      ),
                    )
                  ) : (
                    <Text>Không có bài học</Text>
                  )}
                </Box>
              ))
            ) : (
              <Text fontSize={'16px'} fontWeight={500}>
                Không có bài học
              </Text>
            )}
          </Box>
          {Boolean(
            quantities?.phrase_quantity + quantities?.sentence_quantity,
          ) && renderDailyReview()}
        </Flex>
      )}
      <Modal isModalOpen={isModalOpen} setIsModalOpen={setIsModalOpen} />
      {checkIsAnySectionCompleted() &&
        !dataFeedbackOverview?.FINISH_FIRST_SECTION?.is_completed && (
          <ModalPromoterScore />
        )}
    </Box>
  )
}

export default RoadmapFull
